import React from 'react';
import { LDContext } from 'launchdarkly-js-client-sdk';

import { LAUNCH_DARKLY_ID } from '../../config';
import { withLDProvider, ProviderConfig } from './index';
import { getUserPublicIP } from '../../../utils/user';
import { isAuthenticatedUser } from '../../../utils/user';
import { useLDClient } from 'launchdarkly-react-client-sdk';

const launchDarklyInitialContext: LDContext = {
  kind: 'user',
  key: 'anonymous-user',
  anonymous: true
};
const DEFAULT_CONFIG: ProviderConfig = {
  clientSideID: LAUNCH_DARKLY_ID,
  context: launchDarklyInitialContext
};

interface Props {
  children?: React.ReactNode;
}

/**
 * Initialises the LD User asyncronously. This component allows us to set properties got
 * asyncronously to the anonymous user.
 * @param {Props} props
 */
const AsyncLDUserInitialiser: React.FC<Props> = (props: Props) => {
  const ldClient = useLDClient();

  React.useEffect(() => {
    if (!ldClient) {
      return;
    }

    // Checks if the user isn't authenticated and hasn't got the IP set,
    // then updates the user config in LD with the public IP
    const loadUnauthenticatedUserConfig = async () => {
      const authenticatedUser = await isAuthenticatedUser();

      if (authenticatedUser) {
        return;
      }

      const ip = await getUserPublicIP();
      const context = ldClient.getContext();
      if (ip) {
        ldClient.identify({ ...context, ip });
      }
    };

    loadUnauthenticatedUserConfig();
  }, [ldClient]);

  return <React.Fragment>{props.children}</React.Fragment>;
};

export const LDProvider = withLDProvider<React.JSX.IntrinsicAttributes & React.PropsWithChildren>(DEFAULT_CONFIG)(
  AsyncLDUserInitialiser
);

export const getDefaultLaunchDarklyUser = async (): Promise<LDContext> => {
  const ip = await getUserPublicIP();

  return {
    ...launchDarklyInitialContext,
    ip
  };
};
